import { FC } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Container, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Add, ArrowBack, HelpOutlined } from '@mui/icons-material';
import { ToDateTime } from '@utils/DateUtils';
import { PublicationStatus, QuestionnaireFormSummary } from '@stores/QuestionnaireAdminStore';
import { useQuestionnairesController } from './useQuestionnairesController';

export const publicationStatusColors: Record<PublicationStatus, 'success' | 'default' | 'warning'> = {
  [PublicationStatus.Published]: 'success',
  [PublicationStatus.Retired]: 'default',
  [PublicationStatus.Draft]: 'warning',
};

// The plain-language guide for the business team: what participants see and what adding, removing, moving or
// editing a questionnaire does at each point in a program.
const QUESTIONNAIRES_GUIDE_URL = 'https://claude.ai/artifact/Pu5RRGfxmDPM4Jp79LChzs';

export const formatPublishedAt = (value: string | null): string =>
  value ? ToDateTime(value).toFormat('MM/dd/yyyy hh:mm a') : '—';

const columns: GridColDef<QuestionnaireFormSummary>[] = [
  { field: 'name', headerName: 'Questionnaire', flex: 1.5, minWidth: 200 },
  { field: 'code', headerName: 'Code', flex: 1, minWidth: 140 },
  {
    field: 'version',
    headerName: 'Latest version',
    width: 130,
    valueGetter: (_value, row) => row.latestVersion?.version ?? null,
    valueFormatter: (value: number | null) => (value ? `v${value}` : '—'),
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 130,
    valueGetter: (_value, row) => row.latestVersion?.status ?? null,
    renderCell: (params) =>
      params.value ? (
        <Chip size="small" label={params.value} color={publicationStatusColors[params.value as PublicationStatus]} />
      ) : (
        '—'
      ),
  },
  {
    field: 'publishedAt',
    headerName: 'Published',
    flex: 1,
    minWidth: 170,
    valueGetter: (_value, row) => row.latestVersion?.publishedAt ?? null,
    valueFormatter: (value: string | null) => formatPublishedAt(value),
  },
  { field: 'cohortCount', headerName: 'Cohorts', width: 100, type: 'number' },
];

/** Every questionnaire, with its latest version and how many cohorts ask it. */
export const AdminQuestionnaires: FC = () => {
  const questionnairesPage = useQuestionnairesController();

  if (questionnairesPage.loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container component="section" sx={{ py: 2 }}>
      <Button startIcon={<ArrowBack />} onClick={questionnairesPage.handleBack} sx={{ mb: 1 }}>
        Back to Admin
      </Button>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4">Questionnaires</Typography>
          <Button
            href={QUESTIONNAIRES_GUIDE_URL}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            startIcon={<HelpOutlined />}
            sx={{ px: 0 }}
          >
            How questionnaires work
          </Button>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={questionnairesPage.handleResults}>Results</Button>
          <Button onClick={questionnairesPage.handleSchedules}>Schedules</Button>
          <Button variant="contained" startIcon={<Add />} onClick={questionnairesPage.handleNew}>
            New Questionnaire
          </Button>
        </Box>
      </Box>

      {questionnairesPage.loadFailed ? (
        <Alert severity="error">The questionnaires could not be loaded.</Alert>
      ) : (
        <DataGrid
          rows={questionnairesPage.questionnaires}
          columns={columns}
          getRowId={(row) => row.id}
          density="compact"
          onRowClick={(params) => questionnairesPage.handleOpen(params.row.id)}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          pageSizeOptions={[10, 25, 50]}
          disableRowSelectionOnClick
          autoHeight
          sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
        />
      )}
    </Container>
  );
};
